package com.kaihuoy.components.common

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLAnchorElement
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.*
import com.kaihuoy.web.FirebaseService
import kotlin.js.Date

// Helper to export element content to DOC
fun exportElementToDoc(elementId: String, fileName: String) {
    val element = document.getElementById(elementId) ?: return
    val content = element.innerHTML
    
    val html = """
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
            <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
            <title>$fileName</title>
            <style>
                body { font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.5; color: #1e293b; }
                h2 { color: #1d4ed8; border-bottom: 2px solid #1d4ed8; padding-bottom: 8px; margin-top: 0; }
                .info-grid { display: table; width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                .info-row { display: table-row; }
                .info-cell { display: table-cell; padding: 10px; border: 1px solid #e2e8f0; width: 50%; vertical-align: top; }
                .label { font-size: 10pt; color: #64748b; font-weight: bold; text-transform: uppercase; margin-bottom: 4px; }
                .value { font-size: 12pt; color: #0f172a; font-weight: bold; }
                .section-header { font-size: 14pt; font-weight: bold; color: #1e293b; margin-top: 25px; margin-bottom: 15px; background-color: #f1f5f9; padding: 8px; border-left: 4px solid #1d4ed8; }
                .checklist-table { width: 100%; border-collapse: collapse; }
                .checklist-row { border-bottom: 1px solid #e2e8f0; }
                .checklist-cell { padding: 8px 5px; vertical-align: middle; }
                .item-text { font-size: 11pt; color: #334155; }
                .item-text-bold { font-weight: bold; color: #1d4ed8; font-size: 12pt; }
                .item-sub { padding-left: 20px; font-style: italic; color: #64748b; }
                .status-text { font-weight: bold; text-align: right; text-transform: uppercase; font-size: 10pt; width: 120px; }
                .status-kunnossa { color: #059669; }
                .status-korjattava { color: #dc2626; }
                .status-huom { color: #d97706; }
                .no-print { display: none !important; }
            </style>
        </head>
        <body>
            $content
        </body>
        </html>
    """.trimIndent()

    // Add Byte Order Mark (BOM) for UTF-8 to help Word recognize the encoding
    val blob = Blob(arrayOf("\uFEFF", html), BlobPropertyBag(type = "application/msword;charset=utf-8"))
    val url = window.asDynamic().URL.createObjectURL(blob)
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url.toString()
    a.download = "$fileName.doc"
    document.body?.appendChild(a)
    a.click()
    document.body?.removeChild(a)
    window.asDynamic().URL.revokeObjectURL(url)
}

fun printElementAsNewWindow(elementId: String, title: String) {
    val element = document.getElementById(elementId) ?: return
    val content = element.innerHTML
    val w = window.open("", "_blank", "width=1000,height=800")
    w?.document?.open()
    w?.document?.write("""
        <!DOCTYPE html><html lang='fi'><head>
        <meta charset='UTF-8'><title>$title</title>
        <style>
            body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 20px; margin: 0; }
            * { background: #fff !important; color: #000 !important; box-shadow: none !important; border-color: #ccc !important; }
            .no-print { display: none !important; }
            img { filter: none !important; }
            .checklist-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10pt; }
            .checklist-column { display: flex; flex-direction: column; }
            .checklist-row-print { display: flex; justify-content: space-between; padding: 2pt 4pt; border-bottom: 1px solid #eee; font-size: 8pt; }
            .checklist-row-print.header { font-weight: bold; background: #f0f0f0 !important; margin-top: 4pt; }
            .checklist-row-print .status { width: 60pt; text-align: right; }
            .print-table { width: 100%; border-collapse: collapse; }
            .print-table th, .print-table td { border: 1px solid #ccc; padding: 6px; }
            .only-doc { display: none !important; }
        </style>
        </head><body>$content
        <script>window.onload=function(){window.print();}</script>
        </body></html>
    """.trimIndent())
    w?.document?.close()
}

// StatusBadge apukomponentti
@Composable
fun StatusBadge(status: String, isHeader: Boolean = false) {
    if (status.isBlank()) {
        Span { Text("-") }
        return
    }

    val (bgColor, textColor, icon) = when (status) {
        "Kunnossa" -> Triple(rgba(34, 197, 94, 0.1), KaihuTheme.Green, "circle-check")
        "Korjattava" -> Triple(rgba(239, 68, 68, 0.1), KaihuTheme.Danger, "circle-x")
        "Huom." -> Triple(rgba(245, 158, 11, 0.1), Color("#f59e0b"), "alert-triangle")
        else -> Triple(KaihuTheme.Divider, KaihuTheme.TextSecondary, "circle")
    }

    Span(attrs = {
        style {
            padding(if (isHeader) 6.px else 4.px, if (isHeader) 16.px else 12.px)
            borderRadius(20.px)
            fontSize(if (isHeader) 0.8.em else 0.75.em)
            fontWeight("700")
            backgroundColor(bgColor)
            color(textColor)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            if (isHeader) {
                property("border", "1px solid $textColor")
                property("box-shadow", "0 2px 4px rgba(0,0,0,0.05)")
            }
        }
    }) {
        TIcon(icon, 1.2.em)
        Text(status.uppercase())
    }
}

// InfoBlock apukomponentti
@Composable
fun InfoBlock(label: String, value: String) {
    Div(attrs = { 
        style { 
            marginBottom(16.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            textAlign("center")
        } 
    }) {
        Div(attrs = { 
            style { 
                fontSize(1.1.em)
                color(KaihuTheme.TextPrimary)
                fontWeight("500")
                marginBottom(4.px)
            } 
        }) {
            Text(value.ifBlank { "-" })
        }
        Label(attrs = { 
            style { 
                fontSize(0.85.em)
                color(KaihuTheme.TextPrimary)
                fontWeight("bold")
                display(DisplayStyle.Block)
            } 
        }) {
            Text(label)
        }
    }
}

// InspectionRecordItem
@Composable
fun InspectionRecordItem(
    record: InspectionRecord,
    isAdmin: Boolean,
    isSelected: Boolean = false,
    onClick: () -> Unit,
    onDelete: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var isHovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onClick { onClick() }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
        style {
            padding(24.px)
            borderRadius(16.px)
            marginBottom(16.px)
            backgroundColor(KaihuTheme.Surface)
            cursor("pointer")
            property("transition", "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)")
            property("border", "1px solid ${if (isSelected) KaihuTheme.Primary else KaihuTheme.Border}")
            property("box-shadow", if (isHovered || isSelected) KaihuTheme.ShadowMd else KaihuTheme.ShadowSm)
            
            if (isHovered || isSelected) {
                property("transform", "translateY(-2px)")
            }
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) } }) {
            Div {
                H4(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontSize(1.1.em) } }) { Text(record.machineManufacturerModel) }
                P(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextSecondary); margin(6.px, 0.px) } }) { Text(record.siteName) }
                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); marginTop(12.px) } }) {
                    Span(attrs = { style { fontSize(0.75.em); color(KaihuTheme.TextLight); fontWeight("500") } }) {
                        Text(Date(record.timestamp.toDouble()).toLocaleString("fi-FI"))
                    }
                    if (record.repairNotes.isNotBlank()) {
                        Span(attrs = { style { fontSize(0.7.em); backgroundColor(rgba(239, 68, 68, 0.1)); color(KaihuTheme.Danger); padding(2.px, 8.px); borderRadius(12.px); fontWeight("600") } }) {
                            Text("HUOMIOITA")
                        }
                    }
                }
            }
            if (isAdmin) {
                DelButton(
                    text = "Poista",
                    onClick = {
                        com.kaihuoy.components.common.AppFeedback.confirm(
                            title = "Poistetaanko tarkastus?",
                            message = "Tämä poistaa tarkastusmerkinnän pysyvästi.",
                            confirmText = "Poista",
                            confirmColor = KaihuTheme.Danger
                        ) {
                            scope.launch {
                                FirebaseService.database.reference("inspections").child(record.id).removeValue()
                                onDelete()
                                com.kaihuoy.components.common.AppFeedback.showSuccess("Tarkastus poistettu.")
                            }
                        }
                    },
                    backgroundColor = KaihuTheme.Danger
                )
            }
        }
    }
}

// InductionRecordItem
@Composable
fun InductionRecordItem(
    record: InductionRecord,
    isAdmin: Boolean,
    isSelected: Boolean = false,
    onClick: () -> Unit,
    onDelete: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var isHovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onClick { onClick() }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
        style {
            padding(24.px)
            borderRadius(16.px)
            marginBottom(16.px)
            backgroundColor(KaihuTheme.Surface)
            cursor("pointer")
            property("transition", "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)")
            property("border", "1px solid ${if (isSelected) KaihuTheme.PrimaryTeal else KaihuTheme.Border}")
            property("box-shadow", if (isHovered || isSelected) KaihuTheme.ShadowMd else KaihuTheme.ShadowSm)

            if (isHovered || isSelected) {
                property("transform", "translateY(-2px)")
            }
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) } }) {
            Div {
                H4(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontSize(1.1.em) } }) { Text(record.userName) }
                P(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextSecondary); margin(6.px, 0.px) } }) { Text(record.siteName) }
                P(attrs = { style { fontSize(0.75.em); color(KaihuTheme.TextLight); fontWeight("500"); marginTop(12.px) } }) {
                    Text(Date(record.timestamp.toDouble()).toLocaleString("fi-FI"))
                }
            }
            if (isAdmin) {
                DelButton(
                    text = "Poista",
                    onClick = {
                        com.kaihuoy.components.common.AppFeedback.confirm(
                            title = "Poistetaanko perehdytys?",
                            message = "Tämä poistaa perehdytysmerkinnän pysyvästi.",
                            confirmText = "Poista",
                            confirmColor = KaihuTheme.Danger
                        ) {
                            scope.launch {
                                FirebaseService.database.reference("inductions").child(record.id).removeValue()
                                onDelete()
                                com.kaihuoy.components.common.AppFeedback.showSuccess("Perehdytys poistettu.")
                            }
                        }
                    },
                    backgroundColor = KaihuTheme.Danger
                )
            }
        }
    }
}

@Composable
fun InspectionRecordDetails(record: InspectionRecord) {
    val dateStr = if (record.timestamp > 0) Date(record.timestamp.toDouble()).toLocaleString("fi-FI") else "-"

    Div(attrs = {
        id("print-section")
        style {
            property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(24.px)
            property("border", "1px solid ${KaihuTheme.Border}")
            property("box-shadow", KaihuTheme.ShadowSm)
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(20.px)
        }
    }) {
        // Otsikko + Tulosta
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexStart) } }) {
            Div {
                H3(attrs = { style { margin(0.px, 0.px, 4.px, 0.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("Käyttöönotto- ja määräaikaistarkastus")
                }
                Div(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextLight) } }) { Text(dateStr) }
            }
            Button(attrs = {
                classes("no-print")
                onClick { printElementAsNewWindow("print-section", "Tarkastus — ${record.machineManufacturerModel}") }
                style {
                    padding(8.px, 16.px); borderRadius(10.px); border(0.px)
                    backgroundColor(KaihuTheme.Primary); color(Color.white)
                    cursor("pointer"); fontWeight("600"); fontSize(0.88.em)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
                }
            }) { TIcon("printer"); Text(" Tulosta PDF") }
        }

        // Perustiedot
        Div(attrs = {
            style {
                backgroundColor(KaihuTheme.Background); borderRadius(12.px); padding(16.px)
                property("border", "1px solid ${KaihuTheme.Divider}")
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
                gap(10.px)
            }
        }) {
            listOf(
                "Tarkastaja" to record.userName,
                "Työmaa" to record.siteName,
                "Työmaan osoite" to record.siteAddress,
                "Konetyyppi" to record.machineType,
                "Konemerkki ja -malli" to record.machineManufacturerModel,
                "Koneen numero" to record.serialNumber,
                "Käyttötuntimäärä" to record.operatingHours
            ).filter { it.second.isNotBlank() }.forEach { (label, value) ->
                Div {
                    Div(attrs = { style { fontSize(0.75.em); color(KaihuTheme.TextSecondary); fontWeight("600"); property("text-transform", "uppercase"); property("letter-spacing", "0.04em"); marginBottom(2.px) } }) { Text(label) }
                    Div(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextPrimary); fontWeight("500") } }) { Text(value) }
                }
            }
        }

        // Tarkastuslista
        val sortedChecklist = record.checklist.toList().sortedWith(compareBy { (item, _) ->
            val n = item.trim().takeWhile { it.isDigit() || it == '.' || it == ',' || it == ' ' }.replace(',', '.').trim().split(" ")[0]
            n.toDoubleOrNull() ?: Double.MAX_VALUE
        })

        if (sortedChecklist.isNotEmpty()) {
            Div(attrs = {
                style {
                    backgroundColor(KaihuTheme.Background); borderRadius(12.px)
                    property("border", "1px solid ${KaihuTheme.Divider}"); overflow("hidden")
                }
            }) {
                Div(attrs = {
                    style {
                        padding(8.px, 12.px); backgroundColor(KaihuTheme.Divider)
                        fontWeight("700"); fontSize(0.82.em); color(KaihuTheme.TextSecondary)
                        property("text-transform", "uppercase"); property("letter-spacing", "0.06em")
                    }
                }) { Text("Tarkastuslista") }

                sortedChecklist.forEach { (item, status) ->
                    val trimmed = item.trim()
                    val isMainCategory = trimmed.matches(Regex("^(9|12|20)\\s+[A-ZÄÖÅ].*")) && !trimmed.contains(" - ")
                    val statusLower = status.lowercase()
                    val isOk = statusLower.contains("kunnossa") || statusLower.contains("ok")
                    val isWarn = statusLower.contains("korjattava") || statusLower.contains("huom")

                    if (isMainCategory) {
                        Div(attrs = {
                            style {
                                padding(8.px, 12.px); marginTop(2.px)
                                backgroundColor(KaihuTheme.Divider)
                                fontWeight("700"); fontSize(0.85.em); color(KaihuTheme.TextSecondary)
                            }
                        }) { Text(trimmed) }
                    } else {
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween)
                                padding(8.px, 12.px, 8.px, if (trimmed.startsWith("-") || trimmed.contains(" - ")) 24.px else 12.px)
                                property("border-top", "1px solid ${KaihuTheme.Divider}")
                                if (isWarn) backgroundColor(Color("#2b0d0d")) else if (isOk) backgroundColor(Color("#0d2b1e")) else backgroundColor(Color.transparent)
                            }
                        }) {
                            Span(attrs = { style { fontSize(0.88.em); color(KaihuTheme.TextPrimary); flex(1) } }) { Text(trimmed) }
                            if (status.isNotBlank()) {
                                Span(attrs = {
                                    style {
                                        fontSize(0.75.em); fontWeight("700"); marginLeft(8.px)
                                        property("white-space", "nowrap")
                                        color(if (isWarn) Color("#EF4444") else if (isOk) Color("#10B981") else KaihuTheme.TextSecondary)
                                    }
                                }) { Text(status.uppercase()) }
                            }
                        }
                    }
                }
            }
        }

        // Korjattavat kohteet
        if (record.repairNotes.isNotBlank()) {
            Div(attrs = {
                style {
                    backgroundColor(Color("#2b0d0d")); borderRadius(12.px); padding(14.px)
                    property("border", "1px solid #EF444444")
                }
            }) {
                Div(attrs = { style { fontSize(0.78.em); color(Color("#EF4444")); fontWeight("700"); property("text-transform", "uppercase"); marginBottom(6.px) } }) {
                    Text("Korjattavat kohteet")
                }
                Div(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextPrimary) } }) { Text(record.repairNotes) }
            }
        }
    }
}

@Composable
fun InspectionRowPrint(item: String, status: String) {
    val trimmedItem = item.trim()
    val isMainCategory = when {
        trimmedItem.startsWith("9 ") || trimmedItem.startsWith("9.") -> {
            !trimmedItem.contains(".") && !trimmedItem.contains("-") && !trimmedItem.contains("seisonta") && !trimmedItem.contains("tela") && !trimmedItem.contains("ylävaunu")
            || trimmedItem.equals("9 Jarrujärjestelmä", ignoreCase = true)
        }
        trimmedItem.startsWith("12 ") || trimmedItem.startsWith("12.") -> {
            !trimmedItem.contains(".") && !trimmedItem.contains("-") && !trimmedItem.contains("työ") && !trimmedItem.contains("ajo")
            || trimmedItem.equals("12 Valot", ignoreCase = true)
        }
        trimmedItem.startsWith("20 ") || trimmedItem.startsWith("20.") -> {
            !trimmedItem.contains(".") && !trimmedItem.contains("-") && !trimmedItem.contains("ääni") && !trimmedItem.contains("vilkku")
            || trimmedItem.equals("20 Merkinantolaitteet", ignoreCase = true)
        }
        else -> false
    }
    val isSubItem = trimmedItem.contains(" - ") || trimmedItem.contains(" – ") || trimmedItem.startsWith("-") || (trimmedItem.contains(".") && !isMainCategory)

    Div(attrs = {
        classes("checklist-row-print")
        if (isMainCategory) classes("header")
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(4.px, 8.px)
            property("border-bottom", "1px solid #e2e8f0") // Kevyt harmaa alaviiva
            if (isSubItem) paddingLeft(16.px)
        }
    }) {
        Span(attrs = {
            style {
                flex(1)
                fontSize(if (isMainCategory) 0.9.em else 0.85.em)
                fontWeight(if (isMainCategory) "bold" else "normal")
            }
        }) {
            Text(trimmedItem)
        }
        if (status.isNotBlank()) {
            Span(attrs = {
                classes("status")
                style {
                    fontSize(0.75.em)
                    fontWeight("bold")
                }
            }) {
                Text(status.uppercase())
            }
        }
    }
}

@Composable
fun InductionRecordDetails(record: InductionRecord) {
    Div(attrs = {
        id("print-section")
        style {
            padding(32.px)
            backgroundColor(KaihuTheme.Surface)
            borderRadius(24.px)
            property("box-shadow", KaihuTheme.ShadowMd)
            // Estetään elementin siirtyminen tulostuksessa
            property("transform", "none")
            property("margin", "0 auto")
            maxWidth(1200.px)
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(32.px)
                paddingBottom(16.px)
                property("border-bottom", "2px solid #f1f5f9")
            }
        }) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(20.px)
                }
            }) {
                Img(src = "https://kaihu.oy/logo.png", attrs = { // Oletuslogo
                    style {
                        height(60.px)
                        property("filter", "grayscale(100%)")
                    }
                })
                Div {
                    H1(attrs = { style { margin(0.px); color(Color.black); fontSize(24.px); fontWeight("bold") } }) {
                        Text("TYÖHÖNOPASTUS")
                    }
                    Span(attrs = { style { fontSize(12.px); color(KaihuTheme.TextSecondary) } }) {
                        Text("MAANSIIRTOLIIKE KYÖSTI KAIHU OY")
                    }
                }
            }

            Button(attrs = {
                classes("no-print")
                onClick { printElementAsNewWindow("print-section", "Perehdytys — ${record.userName}") }
                style {
                    padding(10.px, 20.px); borderRadius(12.px)
                    backgroundColor(KaihuTheme.Primary); color(Color.white)
                    border(0.px); cursor("pointer"); fontWeight("600")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                }
            }) { TIcon("printer"); Text(" Tulosta PDF") }
        }

        // 1. Henkilötiedot (Grid-asettelu kuten lomakkeessa)
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(0.px)
                marginBottom(32.px)
                border(1.px, LineStyle.Solid, Color.black)
            }
        }) {
            // Nimi ja Syntymäaika
            Div(attrs = { style { property("border-bottom", "1px solid black"); property("border-right", "1px solid black"); padding(8.px) } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Nimi") }
                Div { Text(record.userName.ifBlank { "-" }) }
            }
            Div(attrs = { style { property("border-bottom", "1px solid black"); padding(8.px) } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Syntymäaika") }
                Div { Text(record.birthDate.ifBlank { "-" }) }
            }
            // Työnantaja ja Veronumero
            Div(attrs = { style { property("border-bottom", "1px solid black"); property("border-right", "1px solid black"); padding(8.px) } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Työnantaja") }
                Div { Text(record.employer.ifBlank { "-" }) }
            }
            Div(attrs = { style { property("border-bottom", "1px solid black"); padding(8.px) } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Veronumero") }
                Div { Text(record.taxNumber.ifBlank { "-" }) }
            }
            // Osoite ja Puh
            Div(attrs = { style { property("border-right", "1px solid black"); padding(8.px) } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Osoite") }
                Div { Text(record.address.ifBlank { "-" }) }
            }
            Div(attrs = { style { padding(8.px) } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Puh.") }
                Div { Text(record.phone.ifBlank { "-" }) }
            }
        }

        // 2. Koulutus / Kurssit
        Div(attrs = {
            style {
                backgroundColor(Color("#e2e8f0"))
                padding(4.px, 8.px)
                fontWeight("bold")
                fontSize(12.px)
                border(1.px, LineStyle.Solid, Color.black)
                property("border-bottom", "none")
            }
        }) { Text("Koulutus / Kurssit / Pätevyydet") }

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr 1fr")
                gap(0.px)
                border(1.px, LineStyle.Solid, Color.black)
                marginBottom(32.px)
            }
        }) {
            listOf("Työturvallisuus", "Tulityö", "Tieturva 1", "Ensiapu", "Vesityökortti", "Tieturva 2").forEach { course ->
                val validity = record.training[course] ?: ""
                Div(attrs = { 
                    style { 
                        padding(8.px)
                        border(1.px, LineStyle.Solid, rgba(0,0,0,0.1))
                    } 
                }) {
                    Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                        Span(attrs = { 
                            style { 
                                color(if (validity.isNotBlank()) KaihuTheme.Green else rgba(0,0,0,0.1))
                                fontSize(1.2.em)
                                fontWeight("bold")
                            } 
                        }) {
                            TIcon(if (validity.isNotBlank()) "checkbox" else "square")
                        }
                        Text(course)
                    }
                    if (validity.isNotBlank()) {
                        Div(attrs = { style { fontSize(10.px); marginTop(4.px) } }) { Text("Voimassa: $validity") }
                    }
                }
            }
        }

        // 3. Perehdytykset
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(32.px)
            }
        }) {
            // Yleinen
            Div {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        backgroundColor(Color("#e2e8f0"))
                        padding(6.px, 12.px)
                        fontWeight("bold")
                        border(1.px, LineStyle.Solid, Color.black)
                    }
                }) {
                    Text("Yleinen perehdyttäminen")
                    Text("Huomattavaa")
                }
                
                val defaultGeneralItems = listOf(
                    "1. Yrityksen esittely, liikeidea, organisaatio",
                    "2. Toimintapolitiikka ja tavoitteet, Laatujärjestelmä",
                    "3. Työajat ja poissaolot",
                    "4. Palkka asiat, lomat",
                    "5. Työterveys",
                    "6. Esimiehen, työtovereiden esittely",
                    "7. Käytettävät lomakkeet",
                    "8. Työsuojeluasioiden esittely",
                    "9. Päihteiden suhteen noudatetaan nollatoleranssia",
                    "10. Poikkeamien käsittely",
                    "11. Työmenetelmät",
                    "12. Koneet (KKH,TRD) ja koneiden käyttö",
                    "13. Hallilla työskentely, sähkötyökalut",
                    "14. Nostoapuvälineet"
                )

                defaultGeneralItems.forEach { item ->
                    ChecklistItem(
                        text = item,
                        isChecked = inductionChecklistContains(record.generalChecked, item),
                        remark = inductionChecklistRemark(record.generalRemarks, item)
                    )
                }
            }

            // Työmaa
            Div {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        backgroundColor(Color("#e2e8f0"))
                        padding(6.px, 12.px)
                        fontWeight("bold")
                        border(1.px, LineStyle.Solid, Color.black)
                    }
                }) {
                    Text("Työmaaperehdyttäminen")
                    Text("Huomattavaa")
                }

                val defaultWorkSiteItems = listOf(
                    "1. Rakennuskohteen/Työmaan/Urakan esittely",
                    "2. Toteutusorganisaatio: Tilaaja, pää-, sivu- ja aliurakoitsijat",
                    "3. Työmaan laatu- ja turvallisuussuunnitelmat",
                    "4. Henkilöstötilat",
                    "5. Työmaan, -paikan järjestys ja siisteys, jätelajittelu",
                    "6. Työterveyshuolto ja ensiapuvalmius",
                    "7. Paloturvallisuus, sammutuskalusto, tulityöt ja tupakointi",
                    "8. Tärkeimmät rakennuskoneet ja käyttösäännöt",
                    "9. Rakennusaikaiset sähköasennukset",
                    "10. Putoamissuojakset, kulkutiet, telineet, portaat ja niiden kunnossapito",
                    "11. Suojakypärän ja muiden suojaimien käyttö, huolto ja säilytys",
                    "12. Terveydelle vaaralliset aineet, materiaalien käyttöturvallisuus",
                    "13. Työntekijän velvollisuus ilmoittaa havaitut puutteet ja viat esimiehelle",
                    "14. Käyttöönotto- ja viikkotarkastukset",
                    "15. Työpaikan työsuojeluorganisaatio",
                    "16. Työmaakierros"
                )

                defaultWorkSiteItems.forEach { item ->
                    ChecklistItem(
                        text = item,
                        isChecked = inductionChecklistContains(record.workSiteChecked, item),
                        remark = inductionChecklistRemark(record.workSiteRemarks, item)
                    )
                }
            }
        }

        // 4. Alatunniste (Allekirjoitukset)
        Div(attrs = {
            style {
                marginTop(48.px)
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr 1fr")
                gap(0.px)
                border(1.px, LineStyle.Solid, Color.black)
            }
        }) {
            Div(attrs = { style { padding(8.px); property("border-right", "1px solid black") } }) {
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Pvm") }
                Div { Text(if (record.timestamp > 0) Date(record.timestamp.toDouble()).toLocaleDateString("fi-FI") else "-") }
            }
            Div(attrs = { style { padding(8.px); property("border-right", "1px solid black"); textAlign("center") } }) {
                Div(attrs = { style { marginTop(24.px); property("border-top", "1px solid rgba(0,0,0,0.2)") } })
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Työntekijä") }
                Div(attrs = { style { fontSize(11.px) } }) { Text(record.userName) }
            }
            Div(attrs = { style { padding(8.px); textAlign("center") } }) {
                Div(attrs = { style { marginTop(24.px); property("border-top", "1px solid rgba(0,0,0,0.2)") } })
                Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text("Perehdyttäjä") }
                Div(attrs = { style { fontSize(11.px) } }) { Text(record.introducerName) }
            }
        }
    }
}

private fun normalizeInductionChecklistText(text: String): String {
    return text
        .lowercase()
        .replace(Regex("^\\s*[0-9]+[.)]?\\s*"), "")
        .replace(Regex("\\btai\\b"), " ")
        .replace(Regex("[^a-zåäö0-9]+"), " ")
        .replace(Regex("\\s+"), " ")
        .trim()
}

private fun inductionChecklistContains(items: List<String>, expectedItem: String): Boolean {
    val normalizedExpected = normalizeInductionChecklistText(expectedItem)
    return items.any { normalizeInductionChecklistText(it) == normalizedExpected }
}

private fun inductionChecklistRemark(remarks: Map<String, String>, expectedItem: String): String {
    val normalizedExpected = normalizeInductionChecklistText(expectedItem)
    return remarks.entries.firstOrNull { normalizeInductionChecklistText(it.key) == normalizedExpected }?.value.orEmpty()
}

@Composable
private fun ChecklistItem(text: String, isChecked: Boolean, remark: String = "") {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            gap(12.px)
            padding(8.px, 0.px)
            property("border-bottom", "1px solid #f1f5f9") // Erittäin kevyt alaviiva
            if (!isChecked) {
                opacity(0.7)
            }
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                flex(1)
            }
        }) {
            Span(attrs = {
                style {
                    color(if (isChecked) KaihuTheme.Green else rgba(0, 0, 0, 0.1))
                    fontWeight("bold")
                    width(24.px)
                    textAlign("center")
                    fontSize(1.2.em)
                }
            }) { 
                if (isChecked) {
                    TIcon("checkbox")
                } else {
                    TIcon("square")
                }
            }
            Span(attrs = { 
                style { 
                    color(if (isChecked) KaihuTheme.TextPrimary else KaihuTheme.TextSecondary)
                    fontSize(0.95.em) 
                } 
            }) { Text(text) }
        }

        if (remark.isNotBlank()) {
            Div(attrs = {
                style {
                    flex(1)
                    padding(4.px, 8.px)
                    backgroundColor(Color("#f8fafc"))
                    borderRadius(4.px)
                    fontSize(0.85.em)
                    fontStyle("italic")
                    color(KaihuTheme.TextSecondary)
                    property("border-left", "3px solid ${KaihuTheme.Primary}")
                    maxWidth(300.px)
                }
            }) {
                Text(remark)
            }
        }
    }
}